import React, { useEffect, useState } from 'react';
import Card from './Card';
import { useSelector, useDispatch } from 'react-redux';
import { selectMyPlayer, playCard, selectIsMyTurn, selectHasAcceptedRoll } from '../../redux/gameSlice';
import { useCallback } from 'react';
import cardList from '../../cardList';
import { showAlert } from '../../redux/overlaySlice';
import { Trans, t } from '@lingui/macro';
import i18n from '../../i18n';

function getPlayerCardsWidth() {
    let calculatedPlayerCardsWidth = window.innerWidth;

    if (calculatedPlayerCardsWidth > 1600) {
        calculatedPlayerCardsWidth = 1600;
    }
    else if (calculatedPlayerCardsWidth < 1320) {
        calculatedPlayerCardsWidth = 1320;
    }

    calculatedPlayerCardsWidth -= 470;
    return calculatedPlayerCardsWidth;
}

export default function PlayerCards() {
    const myPlayer = useSelector(selectMyPlayer);
    const isMyTurn = useSelector(selectIsMyTurn);
    const hasAcceptedRoll = useSelector(selectHasAcceptedRoll);
    const [playerCardsWidth, setPlayerCardsWidth] = useState(getPlayerCardsWidth());
    const [cardFlippingIdx, setCardFlippingIdx] = useState(-1);
    const dispatch = useDispatch();
    const playCardCb = useCallback((idx) => {
        const cardType = myPlayer.cards[idx];
        const card = cardList[cardType];
        if (!isMyTurn || card.use_mode !== 'SINGLE') {
            if (cardFlippingIdx !== -1) {
                return; // Nothing, already flipping
            }

            setCardFlippingIdx(idx);
            setTimeout(() => setCardFlippingIdx(-1), 400);
            return;
        }

        if (!hasAcceptedRoll) {
            dispatch(showAlert({
                canEsc: true,
                alertType: "confirmation",
                alertTitle: i18n._(t`Roll dice first!`),
                alertText: i18n._(t`You can only play cards after you've rolled the dice`),
                alertButtons: [{
                    content: i18n._(t`MAKES SENSE`),
                }]
            }));
            return;
        }

        if (cardType === "medical_treatment" && myPlayer.energy < 1) {
            dispatch(showAlert({
                canEsc: true,
                alertType: "alert",
                alertTitle: i18n._(t`You don't have enough energy!`),
                alertText: i18n._(t`You need at least 1 Energy in order to play this card`),
                alertButtons: [{
                    content: i18n._(t`OKAY`),
                }]
            }));
            return;
        }

        dispatch(playCard(Number(idx)));
    }, [dispatch, myPlayer.energy, myPlayer.cards, isMyTurn, cardFlippingIdx, hasAcceptedRoll]);

    useEffect(() => {
        let timeoutHandler = null;
        const updateWindowWidth = () => {
            clearTimeout(timeoutHandler);
            timeoutHandler = setTimeout(() => setPlayerCardsWidth(getPlayerCardsWidth()), 250);
        }

        window.addEventListener('resize', updateWindowWidth);
        return () => window.removeEventListener('resize', updateWindowWidth);
    });

    let playerCardList = [];
    if (myPlayer.cards) {
        let cardsWithIndex = [];
        for (let i = 0; i < myPlayer.cards.length; ++i) {
            cardsWithIndex.push({
                idx: i,
                cardId: myPlayer.cards[i]
            });
        }

        let sortedCards = cardsWithIndex.sort((cardIdA, cardIdB) => {
            const cardA = cardList[cardIdA.cardId];
            const cardB = cardList[cardIdB.cardId];
            if (cardA.use_mode !== cardB.use_mode) {
                return cardA.use_mode === "KEEP" ? -1 : 1;
            }

            return 0;
        });

        const cardWidth = 195;
        const cardCount = myPlayer.cards.length;
        for (let [j, card] of sortedCards.entries()) {
            const calculatedMargin = cardCount <= playerCardsWidth / (cardWidth + 10) ? "10px" : `calc((100% - ${cardWidth * cardCount}px) / ${cardCount - 1})`;
            const calculatedAppendixWidth = j < cardCount - 1 ? (cardWidth + (playerCardsWidth - (cardWidth * cardCount)) / (cardCount - 1)) + 'px' : '195px';
            playerCardList.push(<Card
                key={card.idx}
                type={card.cardId}
                animating={cardFlippingIdx === card.idx}
                movementType={cardFlippingIdx === card.idx ? "flipping-card" : ""}
                onClick={() => playCardCb(card.idx)}
                marginRight={calculatedMargin}
                appendixWidth={calculatedAppendixWidth}
            />);
        }
    }

    if (playerCardList.length === 0) {
        let line = isMyTurn
            ? <Trans>You currently have no cards in your hand. Buy them from the table in exchange for energy</Trans>
            : <Trans>You currently have no cards in your hand. You can buy them from the table during you turn in exchange for energy</Trans>;
        playerCardList = <div>
            <div className="card fake-card">
                <div className="fake-card-smaller-container">
                    {line}
                </div>
                <div className="card-appendix-hovering"></div>
            </div>
        </div>;
    }

    return <div id="player-cards-container">
        {playerCardList}
    </div>;
}
